import Box from '@components/Box';
import PlacingOrderAddressDropdownItem
  from '../PlacingOrderAddressDropdownItem/PlacingOrderAddressDropdownItem';
import PlacingOrderAddressEnglish from '../PlacingOrderAddressEnglish';
import SelectContainer from '@containers/SelectContainer';
import SelectDropdown from '@components/SelectDropdown';
import TextInputContainer from '@containers/TextInputContainer';
import TextInputWithSuggestionsContainer from '@containers/TextInputWithSuggestionsContainer';
import { AddressTemplate, TLabelSelectedItem } from '@api/apiClient';
import { Column, Row } from '@components/Grid';
import {
  countries,
  countryKeyExtractor,
  CountrySelectOption,
  CountrySelectValue,
  CountryType
} from '@components/CountrySelect';
import { IPlacingOrderAddressFieldsFirstProps } from '../../types';
import { TRegion, TAddress } from '@api/apiClient/address';
import { useTranslation } from 'react-i18next';

const PlacingOrderAddressFieldsFirst: React.FC<IPlacingOrderAddressFieldsFirstProps> = (props: IPlacingOrderAddressFieldsFirstProps): React.ReactElement => {
  const {
    availableCountries,
    control,
    country,
    emptyAddressWithUpdate,
    fullListAddresses,
    handlerGetFullAddress,
    handlerOnClickDeleteAddress,
    handlerSetCurrentAddress,
    handlerSetSelectedSuggestion,
    labelSelectedItem,
    region,
    regions,
    useSuggestions,
    withFullAddressInEnglish
  } = props;

  const allowedIds = availableCountries ?? ['CN', 'HK', 'RU'];

  const { t } = useTranslation();

  const getAddrString = (value: TAddress) => `${value.address} ${value.adcode}`;

  return (
    <>
      <Row>
        <Column phone={12}>
          <Box mb={5}>
            <SelectDropdown<AddressTemplate>
                defaultSelectedItem={emptyAddressWithUpdate}
                items={fullListAddresses}
                keyExtractor={(item: TLabelSelectedItem) => item.id}
                label={t('placingOrderModalWindow.form.address.addressTitle')}
                labelSelectedItem={() => handlerGetFullAddress(labelSelectedItem)}
                onItemSelect={(id: string) => handlerSetCurrentAddress(id)}
                renderItem={(props: { item: TLabelSelectedItem }) => (
                    <PlacingOrderAddressDropdownItem
                        handlerGetFullAddress={handlerGetFullAddress}
                        handlerOnClickDeleteAddress={handlerOnClickDeleteAddress}
                        {...props}
                    />
                )}
            />
          </Box>
        </Column>
      </Row>
      <Row>
        <Column phone={5}>
          <Box
              mb={5}
              pr={4}
          >
            <SelectContainer<CountryType, AddressTemplate>
                control={control}
                disabled={country !== 'unknown'}
                items={countries.filter((item: CountryType) => allowedIds.includes(item.id))}
                keyExtractor={countryKeyExtractor}
                name="address.countryCode"
                renderItem={CountrySelectOption}
                renderSelectedItem={CountrySelectValue}
                title={t('placingOrderModalWindow.form.address.country')}
            />
          </Box>
        </Column>
        <Column phone={7}>
          <Box mb={5}>
            <SelectContainer<TRegion, AddressTemplate>
                control={control}
                items={regions}
                keyExtractor={(province: TRegion | null) => province?.nameCn ?? ''}
                labelOptionItem={(province: TRegion | null) => province?.nameCn ?? ''}
                name="address.region"
                title={t('placingOrderModalWindow.form.address.region')}
            />
          </Box>
        </Column>
      </Row>
      <Row>
        <Column phone={12}>
          <Box mb={5}>
            <TextInputWithSuggestionsContainer
                control={control}
                disabled={!region}
                name="address.cityAndStreet"
                title={t('placingOrderModalWindow.form.address.cityAndStreet')}
                useSuggestions={useSuggestions}
                onSelect={handlerSetSelectedSuggestion}
                onInput={() => (handlerSetSelectedSuggestion && handlerSetSelectedSuggestion(undefined))}
                getName={getAddrString}
                getSelectionValue={(value: TAddress) => value.address}
            />
          </Box>
        </Column>
      </Row>
      <Row>
        <Column phone={2}>
          <Box
              mb={8}
              pr={4}
          >
            <TextInputContainer
                control={control}
                name="address.house"
                title={t('placingOrderModalWindow.form.address.house')}
            />
          </Box>
        </Column>
        <Column phone={3}>
          <Box
              mb={8}
              pr={4}
          >
            <TextInputContainer
                control={control}
                name="address.flat"
                title={t('placingOrderModalWindow.form.address.flat')}
            />
          </Box>
        </Column>
        <Column phone={7}>
          <Box mb={8}>
            <TextInputContainer
                control={control}
                name="address.zipCode"
                title={t('placingOrderModalWindow.form.address.zipCode')}
            />
          </Box>
        </Column>
      </Row>
      {withFullAddressInEnglish && (
          <PlacingOrderAddressEnglish control={control}/>
      )}
    </>
  );
};

export default PlacingOrderAddressFieldsFirst;
